import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { functions, isEqual, omit } from "lodash";
import moment from "moment";
import React, { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useHistory } from "react-router-dom";
import ReactTooltip from "react-tooltip";
import {
  alert_circle,
  bell,
  clipboard,
  flame,
  mail,
  message_square,
  more_horizontal,
  plain_phone_white,
  user,
} from "src/images/NewDesign";
import { useClickOutside } from "src/utils/hooks";
import { NotificationType } from "src/utils/misc";
import { theme } from "src/utils/theme";
import { errorToast } from "src/utils/toast";
import styled from "styled-components";
import { PhoenixStyledTooltip } from "../Dumb/PhoenixStyledTooltip";
import { AppText, FlexDiv, Loading } from "../UI";
import { PhoenixAppButton, PhoenixIcon } from "../UI/Phoenix";

const FETCH_NOTIFICATIONS = gql`
  query fetchNotificationList($take: Int, $skip: Int) {
    fetchNotifications(take: $take, skip: $skip) {
      id
      lead_id
      text_computed
      details
      read
      payload
      send_action_back_to_server
      type
      updated_at
      created_at
      relevant_id
      call_to_action_read
      text_action
      date_string
      type_label
      salesperson
      voicemail_url
      voicemail_url_computed
      can_dial
      phone_number
    }
  }
`;

const TAKE = 25;

export const NotificationList: React.FC<{ setHoistedSetNotifications: Dispatch<SetStateAction<any>> }> = ({
  setHoistedSetNotifications,
}) => {
  const [notificationList, setNotificationList] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [skip, setSkip] = useState<number>(0);

  useEffect(() => {
    fetchNotificationList({
      variables: {
        take: TAKE,
        skip: 0,
      },
    });
  }, []);
  useEffect(() => {
    ReactTooltip.rebuild();
    setHoistedSetNotifications(() => setNotificationList);
  }, [notificationList]);

  const handleShowMore = useCallback(() => {
    fetchNotificationList({
      variables: {
        take: TAKE,
        skip: skip + TAKE,
      },
    });
    setSkip((prevSkip) => prevSkip + TAKE);
  }, [TAKE, skip, setSkip]);

  const [fetchNotificationList, { data: fetchNotificationsData, loading: fetchNotificationsLoading }] = useLazyQuery(
    FETCH_NOTIFICATIONS,
    {
      fetchPolicy: "cache-and-network",
      onCompleted: () => {
        const newList = [...notificationList, ...fetchNotificationsData.fetchNotifications];

        if (fetchNotificationsData?.fetchNotifications?.length < TAKE) setHasMore(false);
        else setHasMore(true);

        setNotificationList(newList);
      },
      onError: () => {
        errorToast("Error fetching notifications");
      },
    },
  );

  return (
    <ListContainer id="notification-scroll-div">
      <InfiniteScroll
        dataLength={notificationList.length}
        next={handleShowMore}
        hasMore={hasMore}
        loader={
          <div style={{ padding: "8px 0px" }}>
            <Loading />
          </div>
        }
        scrollableTarget="notification-scroll-div"
      >
        <PhoenixStyledTooltip id="notification-tooltip" />
        {notificationList.map((notification: any, i: number) => (
          <NotificationItem
            data={notification}
            key={`${notification.id}-${i}`}
            setNotificationList={setNotificationList}
            lastItem={i === notificationList.length - 1}
          />
        ))}
      </InfiniteScroll>

      {!notificationList.length && !fetchNotificationsLoading && (
        <FlexDiv
          width="100%"
          height="100%"
          justify="center"
          align="center"
          direction="column"
          gap={24}
          padding="0px 0px 86px 0px"
        >
          <PhoenixIcon
            svg={bell}
            size={40}
            color={theme.icon.neutral.default}
            hoverColor={theme.icon.neutral.default}
          />
          <AppText fontSize={14} fontWeight={400} lineHeight={20} color={theme.text.neutral.tertiary}>
            No notifications
          </AppText>
        </FlexDiv>
      )}
    </ListContainer>
  );
};

const ListContainer = styled.div`
  max-height: calc(100vh - 152px);
  height: 100%;
  overflow-y: auto;
`;

const NotificationItem: React.FC<{
  data: any;
  setNotificationList: Dispatch<SetStateAction<any[]>>;
  lastItem?: boolean;
}> = React.memo(
  ({ data, setNotificationList, lastItem }) => {
    const [showMenu, setShowMenu] = useState<boolean>(false);

    const history = useHistory();

    const title = useMemo(() => data?.text_computed || data?.type_label || "N/A", [data]);
    const buttonText = useMemo(() => {
      switch (data?.type) {
        case "MissedCall":
          return data?.details ? "View Voicemail" : null;
        case "EmailRecieved":
          return data?.relevant_id && data?.lead_id ? "View Email" : null;
        case "ReassginLead":
        case "ConciergeWidget":
        case "LeadOwnershipConstraint":
        case "HotLead":
          return data?.lead_id ? "View Lead" : null;
        case "LeadAlreadyOwned":
        case "LeadOwnershipConstraint":
        case "NewSMS":
          return data?.relevant_id ? "View Lead" : null;
        case "CallReportReviewedFeedback":
          return "View Report";
        default:
          return null;
      }
    }, [data]);
    const handleClick = useCallback(() => {
      switch (data?.type) {
        case "MissedCall":
          data?.voicemail_url_computed && window.open(data?.voicemail_url_computed, "_blank", "noreferrer");
          return;
        case "HotLead":
        case "EmailRecieved":
          data?.lead_id && history.push(`/lead-detail/${data?.lead_id}`);
          return;
        case "CallReportReviewedFeedback":
          data?.relevant_id && history.push(`/call-report/${data?.relevant_id}`);
          return;
        default:
          history.push(`/lead-detail/${data?.relevant_id}`);
          return;
      }
    }, [data]);

    return (
      <NotificationItemContainer read={data?.read}>
        {showMenu && (
          <NotificationMenu
            id={data?.id || ""}
            setShowMenu={setShowMenu}
            setNotificationList={setNotificationList}
            lastItem={lastItem}
          />
        )}

        {!data?.read && (
          <div
            style={{
              position: "absolute",
              width: "8px",
              height: "8px",
              borderRadius: "8px",
              background: theme.fill.danger.primary,
              left: "8px",
            }}
          />
        )}

        <FlexDiv justify="space-between" align="center" width="100%">
          <FlexDiv gap={12} align="center">
            <NotificationBadge type={data?.type || "Other"} />
            <FlexDiv direction="column">
              <AppText
                fontSize={12}
                fontWeight={400}
                lineHeight={18}
                style={{ maxWidth: "234px", textOverflow: "ellipsis", overflow: "hidden", paddingLeft: "4px" }}
                noWrap
                data-tip={title}
                data-for={title.length > 32 ? "notification-tooltip" : null}
              >
                {title}
              </AppText>
              {!!buttonText && (
                <PhoenixAppButton variant="brand-outline" buttonType="ghost-small" height={18} onClick={handleClick}>
                  {buttonText}
                </PhoenixAppButton>
              )}
            </FlexDiv>
          </FlexDiv>

          <FlexDiv direction="column" gap={4}>
            <FlexDiv gap={4}>
              <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.primary}>
                {moment(data?.created_at).format("MM/DD/YYYY")}
              </AppText>
              <AppText fontSize={12} fontWeight={400} lineHeight={18} color={theme.text.neutral.secondary}>
                {moment(data?.created_at).format("h:mm A")}
              </AppText>
            </FlexDiv>

            <PhoenixIcon
              svg={more_horizontal}
              size={16}
              color={theme.icon.neutral.default}
              hoverColor={theme.icon.neutral.default}
              pointer
              style={{ marginLeft: "auto" }}
              onClick={() => setShowMenu(true)}
            />
          </FlexDiv>
        </FlexDiv>
      </NotificationItemContainer>
    );
  },
  (oldProps, newProps) => {
    const oldP = omit(oldProps, functions(oldProps));
    const newP = omit(newProps, functions(newProps));
    return isEqual(oldP, newP);
  },
);

const NotificationItemContainer = styled.div<{ read: boolean }>`
  position: relative;

  display: flex;
  gap: 8px;
  align-items: center;

  width: 100%;
  height: 72px;
  padding: 16px 16px 16px 24px;

  background-color: ${({ read }) => (read ? theme.fill.neutral.primary : theme.fill.brand.tertiary)};
  border-bottom: 1px solid ${theme.border.neutral.secondary};
`;

const NotificationBadge: React.FC<{ type: NotificationType }> = ({ type }) => {
  const backgroundColor = useMemo(() => {
    switch (type) {
      case "MissedCall":
      case "MissedCallMeNow":
      case "MissedEvent":
        return theme.fill.danger.primary;
      case "NewSMS":
      case "EmailRecieved":
        return theme.fill.brand.inverse;
      case "HotLead":
        return theme.fill.dataviz4.inverse;
      default:
        return theme.fill.brand.secondary;
    }
  }, [type]);
  const icon = useMemo(() => {
    switch (type) {
      case "MissedCall":
      case "MissedCallMeNow":
      case "MissedEvent":
        return plain_phone_white;
      case "NewSMS":
        return message_square;
      case "EmailRecieved":
        return mail;
      case "HotLead":
        return flame;
      case "OpsiqForm":
      case "SellfireForm":
      case "ConciergeWidget":
        return clipboard;
      case "ReassginLead":
      case "LeadImport":
        return user;
      default:
        return alert_circle;
    }
  }, [type]);
  const iconColor = useMemo(() => {
    switch (type) {
      case "MissedCall":
      case "MissedCallMeNow":
      case "MissedEvent":
        return theme.icon.neutral.inverse;
      case "NewSMS":
        return theme.icon.neutral.inverse;
      case "EmailRecieved":
        return theme.icon.neutral.inverse;
      case "HotLead":
        return theme.icon.neutral.inverse;
      default:
        return theme.icon.brand.default;
    }
  }, [type]);

  return (
    <BadgeContainer backgroundColor={backgroundColor}>
      <PhoenixIcon size={14} svg={icon} color={iconColor} hoverColor={iconColor} />
    </BadgeContainer>
  );
};

const BadgeContainer = styled.div<{ backgroundColor: string }>`
  display: flex;
  justify-content: center;
  align-items: center;

  width: 24px;
  height: 24px;

  border-radius: 4px;
  background-color: ${({ backgroundColor }) => backgroundColor};
`;

const DELETE_NOTIFICATION = gql`
  mutation deleteOneNotification($id: String!) {
    deleteOneNotification(id: $id) {
      id
      text
      details
      read
      payload
      date_string
      created_at
      call_to_action_read
      text_action
      relevant_id
      send_action_back_to_server
      type
    }
  }
`;
const MARK_NOTIFICATION_AS_READ = gql`
  mutation markNotificationAsRead($notification_id: String!) {
    markNotificationAsRead(notification_id: $notification_id)
  }
`;

interface INotificationMenu {
  id: string;
  setShowMenu: Dispatch<SetStateAction<boolean>>;
  setNotificationList: Dispatch<SetStateAction<any[]>>;
  lastItem?: boolean;
}

const NotificationMenu: React.FC<INotificationMenu> = ({ id, setShowMenu, setNotificationList, lastItem }) => {
  const containerRef = useRef(null);

  useClickOutside(containerRef, () => {
    setShowMenu(false);
  });

  const [deleteOneNotification] = useMutation(DELETE_NOTIFICATION, {
    onCompleted({ deleteOneNotification }) {
      console.log("Deleted notification: ", deleteOneNotification);
      if (!deleteOneNotification) {
        return;
      }
    },
    onError({ message }) {
      console.log("Error in deleteOneNotification: ", message);
      errorToast("Error deleting notification");
    },
  });
  const [markNotificationAsRead] = useMutation(MARK_NOTIFICATION_AS_READ, {
    onCompleted({ markNotificationAsRead }) {
      if (!markNotificationAsRead) {
        return;
      }
    },
    onError({ message }) {
      console.log("Error in markNotificationAsRead: ", message);
    },
  });

  const handleDelete = useCallback(() => {
    setShowMenu(false);
    setNotificationList((prevList) => prevList.filter((item) => item.id !== id));
    deleteOneNotification({ variables: { id: id } });
  }, []);

  const handleMarkAsRead = useCallback(() => {
    setShowMenu(false);
    setNotificationList((prevList) =>
      prevList.map((item) => {
        if (item.id === id) {
          return { ...item, read: true };
        }
        return item;
      }),
    );
    markNotificationAsRead({ variables: { notification_id: id } });
  }, [setShowMenu, setNotificationList, markNotificationAsRead, id]);

  return (
    <PopupContainer ref={containerRef} lastItem={lastItem}>
      <MenuItem onClick={handleMarkAsRead}>Mark as Read</MenuItem>
      <MenuItem onClick={handleDelete}>Delete</MenuItem>
    </PopupContainer>
  );
};

const PopupContainer = styled.div<{ lastItem?: boolean }>`
  position: absolute;
  top: ${({ lastItem }) => (lastItem ? "initial" : "16px")};
  bottom: ${({ lastItem }) => (lastItem ? "12px" : "initial")};
  right: 42px;
  z-index: 10;

  background-color: ${theme.WHITE_COLOR};
  box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.15);
  border: 1px solid ${theme.NEUTRAL300};
  border-radius: 4px;
  overflow: hidden;

  animation: ${theme.popup} 0.2s ease;
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;

  width: 152px;
  height: 40px;
  padding: 8px;

  font-size: 12px;
  line-height: 18px;
  cursor: pointer;
  color: ${theme.BLACK_COLOR};

  transition: background-color 0.15s ease, color 0.15s ease;
  z-index: 1;

  :hover {
    background-color: ${theme.PRIMARY100};
    color: ${theme.PRIMARY500};
  }
`;
